import { NavigateFunction } from 'react-router-dom';

// Navigation paths
export const ROUTES = {
	HOME: '/',
	LOGIN: '/login',
	FORGOT_PASSWORD: '/forgot-password',
	RESET_PASSWORD: '/reset-password',
	SET_PASSWORD: '/set-password',
	ADMIN_DASHBOARD: '/admin',
	MANAGER_DASHBOARD: '/manager',
	WORKER_DASHBOARD: '/worker',
	NOT_FOUND: '/404',
} as const;

/**
 * Get the appropriate dashboard path based on user role
 * @param userRole - The role of the user ('admin', 'worker', 'manager')
 * @returns The path to redirect to
 */
export const getDashboardPath = (userRole?: string): string => {

	switch (userRole) {
		case 'admin':
			return ROUTES.ADMIN_DASHBOARD;
		case 'worker':
		case 'field_worker':
			return ROUTES.WORKER_DASHBOARD;
		case 'manager':
			return ROUTES.MANAGER_DASHBOARD;
		default:
			return ROUTES.WORKER_DASHBOARD;
	}
};

// Navigation helper functions
export const navigateToRoleBasedRoute = (
	navigate: NavigateFunction,
	role: 'admin' | 'worker' | 'manager'
) => {
	const targetRoute = getDashboardPath(role);
	navigate(targetRoute);
};

export const navigateToLogin = (navigate: NavigateFunction) => {
	navigate(ROUTES.LOGIN);
};

export const navigateToHome = (navigate: NavigateFunction) => {
	navigate(ROUTES.HOME);
};

// Check if user can access a specific route
export const canAccessRoute = (
	userRole: 'admin' | 'worker' | 'manager' | null,
	requiredRole: 'admin' | 'worker' | 'manager'
): boolean => {
	if (!userRole) return false;
	return userRole === requiredRole;
};

// Get default route for user
export const getDefaultRoute = (role: 'admin' | 'worker' | 'manager' | null): string => {
	if (!role) return ROUTES.LOGIN;
	return getDashboardPath(role);
};

// Get role display name
export const getRoleDisplayName = (role: 'admin' | 'worker' | 'manager' | string): string => {
	switch (role) {
		case 'admin':
			return 'Admin';
		case 'worker':
		case 'field_worker':
			return 'Worker';
		case 'manager':
			return 'Manager';
		default:
			return 'Worker';
	}
};

/**
 * Check if a user has admin privileges
 * @param user - The user object
 * @returns true if user is admin, false otherwise
 */
export const isAdmin = (user?: any | null): boolean => {
	return user?.role === 'admin';
};

/**
 * Check if a user has any authenticated privileges
 * @param user - The user object
 * @returns true if user exists, false otherwise
 */
export const isUser = (user?: any | null): boolean => {
	return user !== null && user !== undefined;
};
